import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { CONTACTS } from "./data";

const API = process.env.REACT_APP_BACKEND_URL;

const getSid = () => {
  let id = localStorage.getItem("fpc-chat-sid");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("fpc-chat-sid", id);
  }
  return id;
};

const WELCOME = "Ciao! Sono l'assistente virtuale di Furlan Print Copy. Chiedimi pure di noleggio, assistenza tecnica, toner, carta e orari.";
const ERR = "Assistente momentaneamente non disponibile. Scrivici su WhatsApp al 346 086 8653.";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("assistant");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [quoteSummary, setQuoteSummary] = useState(null);
  const scrollRef = useRef(null);

  const loadHistory = async (m) => {
    try {
      const res = await fetch(`${API}/api/chat/history/${getSid()}?mode=${m}`);
      const data = await res.json();
      return data.messages || [];
    } catch {
      return [];
    }
  };

  const send = async (text, m = mode) => {
    const clean = text.trim();
    if (!clean || busy) return;
    setInput("");
    setBusy(true);
    setMessages((prev) => [...prev, { role: "user", text: clean }, { role: "assistant", text: "" }]);
    const pushDelta = (d) =>
      setMessages((prev) => {
        const c = [...prev];
        c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + d };
        return c;
      });
    try {
      const res = await fetch(`${API}/api/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSid(), message: clean, mode: m }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop();
        for (const ch of chunks) {
          const line = ch.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5));
          if (payload.delta) pushDelta(payload.delta);
          if (payload.error) {
            setMessages((prev) => {
              const c = [...prev];
              c[c.length - 1] = { role: "assistant", text: payload.error };
              return c;
            });
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const c = [...prev];
        c[c.length - 1] = { role: "assistant", text: ERR };
        return c;
      });
    }
    setBusy(false);
  };

  const switchMode = async (m) => {
    if (m === mode) return;
    setMode(m);
    setQuoteSummary(null);
    const hist = await loadHistory(m);
    if (m !== "assistant" && hist.length === 0) {
      setMessages([]);
      const kickoff = m === "setup" ? "Vorrei organizzare la mia postazione di lavoro" : "Vorrei un preventivo per il mio ufficio";
      send(kickoff, m);
    } else {
      setMessages(m === "assistant" && hist.length === 0 ? [{ role: "assistant", text: WELCOME }] : hist);
    }
  };

  const openPanel = async () => {
    setOpen(true);
    const hist = await loadHistory(mode);
    if (hist.length > 0) setMessages(hist);
    else setMessages((prev) => (prev.length ? prev : [{ role: "assistant", text: WELCOME }]));
  };

  useEffect(() => {
    if (mode !== "assistant") {
      const last = [...messages].reverse().find((m) => m.role === "assistant" && m.text);
      if (last && last.text.includes("[PREVENTIVO]")) {
        setQuoteSummary(last.text.split("[PREVENTIVO]")[1].trim());
      }
    }
  }, [messages, mode]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  const waQuoteUrl = quoteSummary
    ? `https://wa.me/${CONTACTS.mobileRaw.replace("+", "")}?text=${encodeURIComponent("Buongiorno! Vorrei un preventivo per il mio ufficio. " + quoteSummary)}`
    : null;

  return (
    <>
      <button
        data-testid="chat-toggle-button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-label="Apri l'assistente virtuale"
        className="fixed bottom-6 left-6 z-50 h-14 px-5 rounded-full bg-[#181817] text-[#FBF9F5] flex items-center gap-2.5 shadow-lg hover:bg-[#C85A32] transition-colors duration-300"
      >
        <MessageSquare size={20} />
        <span className="text-sm hidden sm:inline">Assistente</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 left-4 sm:left-6 z-50 w-[calc(100vw-2rem)] max-w-[380px] h-[520px] max-h-[68vh] bg-[#FBF9F5] border border-[#E5E2DC] rounded-sm shadow-[0_30px_60px_-20px_rgba(24,24,23,0.35)] flex flex-col overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-[#E5E2DC] flex items-center justify-between bg-[#F3F0EA]">
              <div className="flex items-center gap-3">
                <img src="/logo-mark.png" alt="" className="w-8 h-8 rounded-sm" />
                <div>
                  <p className="text-sm font-medium text-[#181817]">Assistente Furlan</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6E6D69]">Risponde subito</p>
                </div>
              </div>
              <button data-testid="chat-close-button" onClick={() => setOpen(false)} aria-label="Chiudi chat" className="text-[#6E6D69] hover:text-[#181817]">
                <X size={18} />
              </button>
            </div>

            <div className="px-4 pt-3 flex gap-1.5">
              <button
                data-testid="chat-mode-assistant"
                onClick={() => switchMode("assistant")}
                className={`flex-1 py-2 rounded-full text-[11px] transition-colors duration-300 ${mode === "assistant" ? "bg-[#181817] text-[#FBF9F5]" : "bg-[#EBE6DD] text-[#6E6D69] hover:text-[#181817]"}`}
              >
                Domande
              </button>
              <button
                data-testid="chat-mode-quote"
                onClick={() => switchMode("quote")}
                className={`flex-1 py-2 rounded-full text-[11px] transition-colors duration-300 ${mode === "quote" ? "bg-[#C85A32] text-[#FBF9F5]" : "bg-[#EBE6DD] text-[#6E6D69] hover:text-[#181817]"}`}
              >
                Preventivo
              </button>
              <button
                data-testid="chat-mode-setup"
                onClick={() => switchMode("setup")}
                className={`flex-1 py-2 rounded-full text-[11px] transition-colors duration-300 ${mode === "setup" ? "bg-[#4A5446] text-[#FBF9F5]" : "bg-[#EBE6DD] text-[#6E6D69] hover:text-[#181817]"}`}
              >
                Configura ufficio
              </button>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" data-lenis-prevent className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${m.role}-${i}`}
                  className={m.role === "user"
                    ? "self-end max-w-[85%] bg-[#181817] text-[#FBF9F5] px-4 py-2.5 rounded-2xl rounded-br-sm text-sm leading-relaxed"
                    : "self-start max-w-[85%] bg-white border border-[#E5E2DC] text-[#181817] px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm leading-relaxed"}
                >
                  {m.role === "assistant" ? m.text.split("[PREVENTIVO]")[0] : m.text}
                  {m.role === "assistant" && m.text === "" && busy && (
                    <Loader2 size={15} className="animate-spin text-[#6E6D69]" />
                  )}
                </div>
              ))}
              {quoteSummary && waQuoteUrl && (
                <a
                  data-testid="chat-whatsapp-cta"
                  href={waQuoteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="self-stretch text-center bg-[#25D366] text-white px-4 py-3 rounded-sm text-sm font-medium hover:opacity-90 transition-opacity duration-300"
                >
                  Invia la richiesta su WhatsApp
                </a>
              )}
            </div>

            <div className="p-3 border-t border-[#E5E2DC] flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send(input)}
                placeholder={mode === "assistant" ? "Scrivi la tua domanda..." : "Rispondi alla domanda..."}
                className="flex-1 bg-white border border-[#E5E2DC] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#181817] transition-colors duration-300"
              />
              <button
                data-testid="chat-send-button"
                onClick={() => send(input)}
                disabled={busy || !input.trim()}
                aria-label="Invia messaggio"
                className="w-10 h-10 rounded-full bg-[#181817] text-[#FBF9F5] flex items-center justify-center hover:bg-[#C85A32] transition-colors duration-300 disabled:opacity-40"
              >
                <Send size={15} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
